import { Trans, useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Link } from "react-router-dom";

import { ScrollArea } from "@/components/ui/scroll-area";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";
import AppLogo from "@/components/Logo.tsx";
import PATHS from "@/routes/paths";

const PrivacyPolicy = () => {
  useTranslation();

  return (
    <div className="bg-background min-h-dvh">
      <div className="absolute top-2 right-2 z-50 md:top-10 md:right-10">
        <LanguageSwitcher />
      </div>
      <ScrollArea className="h-dvh">
        <div className="mx-auto max-w-4xl p-6">
          <motion.div
            animate={{ opacity: 0.05 }}
            className="absolute -top-20 -left-20 h-56 w-56 rounded-full bg-gradient-to-br from-teal-400 to-purple-500"
            initial={{ opacity: 0.05 }}
            layoutId={"blue"}
            transition={{ layout: { duration: 0.75 } }}
          />
          <motion.div
            animate={{ opacity: 0.05 }}
            className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-gradient-to-tr from-red-400 to-pink-600"
            initial={{ opacity: 0.05 }}
            layoutId={"purple"}
            transition={{ layout: { duration: 0.75 } }}
          />
          <div className="relative">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header with Logo */}
              <div className="py-8 text-center">
                <AppLogo className="mx-auto mb-6 h-12" />
                <h1 className="text-accent-foreground mb-4 text-3xl font-bold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="title"
                    ns="privacy_policy"
                  />
                </h1>
                <p className="text-muted-foreground text-sm">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="legalNote"
                    ns="privacy_policy"
                  />
                </p>
              </div>

              {/* Section 1 - General Provisions */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section1.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 3 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: pointNumber * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                          link1: (
                            //@ts-ignore
                            <a
                              key="1"
                              className="text-accent-foreground hover:text-accent-foreground/80 underline"
                              href="mailto:support@ximly.app"
                            />
                          ),
                          link2: (
                            <Link
                              className="text-accent-foreground hover:text-accent-foreground/80 underline"
                              to={PATHS.TERMS_OF_SERVICE}
                            />
                          ),
                        }}
                        //@ts-ignore
                        i18nKey={`section1.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 2 - Types of Processed Data */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section2.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 8 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 3) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                        }}
                        //@ts-ignore
                        i18nKey={`section2.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 1 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section2.subpoint1_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 2 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d", "e"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section2.subpoint2_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 3 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section2.subpoint3_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 6 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section2.subpoint6_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 7 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section2.subpoint7_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 3 - Processing of Conversations */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section3.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 11) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                        }}
                        //@ts-ignore
                        i18nKey={`section3.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 3 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d", "e"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section3.subpoint3_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 4 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section3.subpoint4_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 4 - Sharing of Personal Data */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section4.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 3 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 16) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                        }}
                        //@ts-ignore
                        i18nKey={`section4.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 1 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d", "e", "f", "g"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section4.subpoint1_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 5 - Rights of Data Subjects */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section5.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 7 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 19) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                          link1: (
                            //@ts-ignore
                            <a
                              key="1"
                              className="text-accent-foreground hover:text-accent-foreground/80 underline"
                              href="mailto:support@ximly.app"
                            />
                          ),
                          link3: (
                            //@ts-ignore
                            <a
                              key="3"
                              className="text-accent-foreground hover:text-accent-foreground/80 underline"
                              href="mailto:kancelaria@uodo.gov.pl"
                            />
                          ),
                        }}
                        //@ts-ignore
                        i18nKey={`section5.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 1 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d", "e", "f", "g"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section5.subpoint1_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 6 - Use of Cookies */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section6.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 6 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 26) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                        }}
                        //@ts-ignore
                        i18nKey={`section6.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 2 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section6.subpoint2_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                      {pointNumber === 4 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section6.subpoint4_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>

              {/* Section 7 - Final Provisions */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="section7.title"
                    ns="privacy_policy"
                  />
                </h2>
                <ol className="space-y-4">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((pointNumber) => (
                    <motion.li
                      key={pointNumber}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3, delay: (pointNumber + 32) * 0.05 }}
                    >
                      <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                      <Trans
                        components={{
                          b: <span key={"3"} className={"font-bold"} />,
                        }}
                        //@ts-ignore
                        i18nKey={`section7.point${pointNumber}`}
                        ns="privacy_policy"
                      />
                      {pointNumber === 2 && (
                        <ul className="mt-2 ml-6 space-y-2">
                          {["a", "b", "c", "d", "e", "f", "g"].map((subpoint) => (
                            <li key={subpoint} className="text-sm">
                              <Trans
                                components={{
                                  b: <span key={"3"} className={"font-bold"} />,
                                }}
                                //@ts-ignore
                                i18nKey={`section7.subpoint2_${subpoint}`}
                                ns="privacy_policy"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </motion.li>
                  ))}
                </ol>
              </div>
            </motion.div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default PrivacyPolicy;
